import React, { useCallback, useContext, useMemo } from 'react';
import { Alert, ConfigProvider } from 'antd';
import type { AlertProps as AntAlertProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { parseClickAction, createCommand } from '../../base/actionParser';
import { DataSetInheritContext, useResolvedDataSet } from '../../base/DataSetInheritContext';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

interface AlertSemanticStyles {
  root?: React.CSSProperties;
  icon?: React.CSSProperties;
  section?: React.CSSProperties;
  title?: React.CSSProperties;
  description?: React.CSSProperties;
  actions?: React.CSSProperties;
  close?: React.CSSProperties;
}

const RESERVED_STORE_PROP_KEYS = new Set([
  'title',
  'message',
  'description',
  'closeAction',
  'dataSet',
  'dataSetField',
  'styles',
  'token',
]);

const parseJson = <T,>(value: T | string | undefined, fallback: T): T => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'string') {
    try {
      const trimmed = value.trim();
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        return JSON.parse(trimmed);
      }
    } catch {
      return fallback;
    }
  }
  return value as T;
};

const kebabToCamel = (str: string): string =>
  str.replace(/-([a-z])/g, (_, c) => c.toUpperCase());

const normalizeStyleKeys = (
  obj: Record<string, unknown> | undefined | null
): Record<string, unknown> => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj ?? {};
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [kebabToCamel(k), v])
  );
};

/**
 * AndAlert Props 接口
 */
export interface AndAlertProps extends Omit<AntAlertProps, 'message' | 'styles'>, BaseComponentProps, DataSetConfig {
  /** 关闭动作 */
  closeAction?: string;
  /** 标题 (对应旧的 message) */
  title?: React.ReactNode;
  /** 关闭文字 (已废弃，兼容用) */
  closeText?: string;
  /** Semantic DOM 样式 */
  styles?: AlertSemanticStyles | string;
  /** Design Token */
  token?: Record<string, unknown> | string;
  /** 迭代器内行下标，供 closeAction 中 @index 占位 */
  index?: number;
}

/**
 * AndAlert 组件
 * 封装 Ant Design Alert，支持数据绑定
 */
export const AndAlert: React.FC<AndAlertProps> = ({
  id,
  sessionId,
  develop,
  closeAction,
  onClose,
  dataSet,
  dataSetField,
  title: propTitle,
  description: propDescription,
  closeText,
  closable,
  styles: propStyles,
  token: propToken,
  index,
  ...restProps
}) => {
  const addCommand = useAndStore((state) => state.addCommand);
  const ctx = useContext(DataSetInheritContext);
  const effectiveIndex = index ?? ctx?.index;
  const effectiveParentIndex = ctx?.parentIndex;

  const resolved = useResolvedDataSet(dataSet, dataSetField, sessionId);
  const effectiveDataSet = resolved.dataSet;
  const effectiveDataSetField = resolved.dataSetField;
  const effectiveSessionId = resolved.sessionId;

  const isDataSetBound = Boolean(
    effectiveDataSet &&
      effectiveDataSetField &&
      String(effectiveDataSetField).trim() !== ''
  );

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(effectiveSessionId, effectiveDataSet),
    [effectiveSessionId, effectiveDataSet]
  );

  const storeProps = useAndStore((s) =>
    effectiveSessionId && id
      ? s.propsStore[`${effectiveSessionId}_props`]?.[id] ?? null
      : null
  );

  const storeValue = useAndStore((s) =>
    dataSetKey && effectiveDataSetField
      ? s.getDataSet(dataSetKey, effectiveDataSetField)
      : undefined
  );

  const computedTitle = useMemo(() => {
    if (develop) return propTitle;
    if (storeProps?.title !== undefined) return storeProps.title;
    if (storeProps?.message !== undefined) return storeProps.message;
    return propTitle;
  }, [develop, storeProps?.title, storeProps?.message, propTitle]);

  const computedDescription = useMemo(() => {
    if (develop) return propDescription;
    if (isDataSetBound) {
      if (storeValue !== undefined && storeValue !== null && storeValue !== '') {
        return String(storeValue);
      }
      if (storeProps?.description !== undefined) return storeProps.description;
      return propDescription;
    }
    if (storeProps?.description !== undefined) return storeProps.description;
    return propDescription;
  }, [
    develop,
    isDataSetBound,
    storeValue,
    storeProps?.description,
    propDescription,
  ]);

  const mergedProps = useMemo(() => {
    const baseProps = { ...restProps };
    if (storeProps && typeof storeProps === 'object') {
      Object.keys(storeProps).forEach((key) => {
        if (
          !RESERVED_STORE_PROP_KEYS.has(key) &&
          storeProps[key] !== undefined &&
          storeProps[key] !== null
        ) {
          (baseProps as Record<string, unknown>)[key] = storeProps[key];
        }
      });
    }
    return baseProps;
  }, [restProps, storeProps]);

  const styles = useMemo(() => {
    const rawStyles = storeProps?.styles ?? propStyles;
    const parsed = parseJson(rawStyles, {} as AlertSemanticStyles);
    return Object.fromEntries(
      Object.entries(parsed).map(([section, styleObj]) => [
        section,
        normalizeStyleKeys(styleObj as Record<string, unknown>),
      ])
    ) as AlertSemanticStyles;
  }, [propStyles, storeProps?.styles]);

  const token = useMemo(() => {
    const rawToken = storeProps?.token ?? propToken;
    return parseJson(rawToken, {} as Record<string, unknown>);
  }, [propToken, storeProps?.token]);

  const computedClosable = useMemo(() => {
    if (closeText) {
      return { closeIcon: <span>{closeText}</span> };
    }
    return storeProps?.closable ?? closable;
  }, [closeText, closable, storeProps?.closable]);

  const effectiveCloseAction = storeProps?.closeAction ?? closeAction;

  const handleClose = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClose?.(e);

      if (effectiveCloseAction && effectiveSessionId) {
        const { actionName, arg } = parseClickAction(
          effectiveCloseAction,
          {
            id,
            index: effectiveIndex,
            parentIndex: effectiveParentIndex,
            dataSet: effectiveDataSet,
            dataSetField: effectiveDataSetField,
            ...mergedProps,
          },
          ctx
        );
        addCommand(effectiveSessionId, createCommand(actionName, arg));
      }
    },
    [
      onClose,
      effectiveCloseAction,
      effectiveSessionId,
      id,
      effectiveIndex,
      effectiveParentIndex,
      effectiveDataSet,
      effectiveDataSetField,
      mergedProps,
      ctx,
      addCommand,
    ]
  );

  const alertElement = (
    <Alert
      {...mergedProps}
      title={computedTitle}
      description={computedDescription}
      closable={computedClosable}
      styles={styles}
      onClose={handleClose}
    />
  );

  if (token && Object.keys(token).length > 0) {
    return (
      <ConfigProvider theme={{ components: { Alert: token } }}>
        {alertElement}
      </ConfigProvider>
    );
  }

  return alertElement;
};

export default AndAlert;
